import { Component, NgZone } from '@angular/core';
import { ScanService } from '../../../providers/scan-service/scan-service';
import { EventLoggerService } from '../../../providers/event-logger/event-logger-service';
import { LoaderService, ToasterService } from '@arianeeprivate/wallet-shared-components';
import { ArianeeService } from '../../../providers/arianee-service/arianee.service';
import { TranslateService } from '@ngx-translate/core';
import { HandleLinkService } from '../../../providers/scan-service/handle-link.service/handle-link.service';
import { Router } from '@angular/router';
import { NavController, Platform } from '@ionic/angular';
import { catchError, delay, distinctUntilChanged, filter, mergeMap, take, tap } from 'rxjs/operators';
import { ethers } from 'ethers';
import { BehaviorSubject, from, Observable, Subscription } from 'rxjs';
import { UserService } from '../../../providers/user-service/user.service';
import { NETWORK } from '@arianee/arianeejs';

@Component({
  selector: 'app-scan-v2',
  templateUrl: './scan-v2.page.html',
  styleUrls: ['./scan-v2.page.scss']
})
export class ScanV2Page {
  selectedMode: 'QR_CODE' | 'NFC' = 'QR_CODE';
  subscriptions: Subscription[] = [];
  sub: Subscription;
  hasLight: Promise<any>;
  isIosNFCEnabled: Promise<boolean | Promise<any>>;

  private isProcessingScan: boolean = false;

  constructor (
    private scanService: ScanService,
    private eventLogger: EventLoggerService,
    private toasterService: ToasterService,
    private arianeeService: ArianeeService,
    private translateService: TranslateService,
    private handleLinkService: HandleLinkService,
    private router: Router,
    private navCtrl: NavController,
    private platform: Platform,
    private loaderService: LoaderService,
    private ngZone: NgZone,
    private userService: UserService
  ) { }

  async ionViewDidEnter () {
    this.eventLogger.logScreen('scanFullScreen', {});

    this.activateQr();
    this.activateQrScan();
  }

  activateQr () {
    if (!this.sub) {
      this.sub = this.scanService.$scanEvents
        .pipe(
          filter(() => !this.isProcessingScan && this.router.url === '/tab/scan-full-page'),
          tap(() => {
            this.isProcessingScan = true;

            // Smoothing UX
            this.ngZone.run(() => { this.loaderService.showLoader(); });
          }),
          delay(750),
          mergeMap((link) => {
            if (ethers.utils.isValidMnemonic(<any>link)) {
              return this.handleMenmonic(<any>link);
            } else {
              return this.handleCertificateLink(link).toPromise();
            }
          })
        )
        .subscribe();

      this.subscriptions.push(this.sub);
    }

    if (this.platform.is('ios')) {
      this.isIosNFCEnabled = this.scanService.isNFCEnabled();
    }

    const sub = this.scanService.$scanActivated.subscribe(() => {
      this.hasLight = this.scanService.hasLight();
    });
    this.subscriptions.push(sub);
  }

  onScanProcessed = async ({ shutdownCamera }: { shutdownCamera: boolean } = { shutdownCamera: true }) => {
    if (shutdownCamera) await this.scanService.disableQrScanner();
    this.isProcessingScan = false;

    console.log('ScanProcessed', { shutdownCamera });
  };

  handleMenmonic (mnemonic): Observable<any> {
    this.ngZone.run(() => this.loaderService.dismiss());

    const onOkHandler = () => {
      this.arianeeService.switchWallet(mnemonic)
        .pipe(
          tap(() => this.eventLogger.logEvent('scan_switchWallet_success')),
          take(1),
          mergeMap(() => from(this.router.navigate(['tab/brand-list']))),
          tap(() => { this.onScanProcessed({ shutdownCamera: true }); })
        ).subscribe();
    };

    const cancelHandler = () => { this.onScanProcessed({ shutdownCamera: false }); };

    return this.toasterService.alert({
      backdropDismiss: false,
      message: 'Scan.importMnemonic.message',
      buttons: [
        {
          text: this.translateService.instant('Scan.importMnemonic.Ok'),
          cssClass: 'secondary',
          role: 'ok',
          handler: onOkHandler
        },
        {
          text: this.translateService.instant('Scan.importMnemonic.Cancel'),
          cssClass: 'secondary',
          role: 'cancel',
          handler: cancelHandler
        }
      ]
    });
  }

  handleCertificateLink (link): Observable<any> {
    return this.handleLinkService.handleLink(link, this.onScanProcessed)
      .pipe(
        catchError((err) => {
          console.error('HandleCertificateLinkError', err);
          setTimeout(() => this.onScanProcessed({ shutdownCamera: false }), 1500);
          return new BehaviorSubject({ certificateId: -1 });
        }),
        filter(i => i.certificateId !== -1 && i !== false),
        tap(objectLink => {
          this.eventLogger.logEvent('scanned', { certificateId: objectLink.certificateId, method: objectLink.method });

          const network = objectLink.network || NETWORK.mainnet;
          if (objectLink.certificateId) {
            this.redirectTo(`/tab/scan/product-detail/${network}/${objectLink.certificateId}/${objectLink.passphrase}/${objectLink.method}`);
          }
        }),
        tap(() => {
          this.onScanProcessed({ shutdownCamera: true });
        })
      );
  }

  async ionViewWillLeave () {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.sub = undefined;
    await this.scanService.disableQrScanner();
  }

  activateQrScan () {
    this.selectedMode = 'QR_CODE';
    this.scanService.enableQrScanner();
  }

  async scanNFC () {
    await this.scanService.scanNFC();
    this.isIosNFCEnabled = this.scanService.isNFCEnabled();
    this.selectedMode = 'NFC';
  }

  redirectTo (url) {
    this.navCtrl.navigateForward(url);
  }
}
